import { useState } from "react";
import { format } from "date-fns";
import { Star, ChevronDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DATE_LOCALES, useI18n } from "@/lib/i18n";
import { money } from "@/lib/booking";
import { BookingCalendar, useIsWide } from "./DatesSection";
import { GuestPicker } from "./GuestPicker";

export const Breakdown = ({ b }) => {
  const { t } = useI18n();
  if (!b.nights) return null;
  return (
    <div className="mt-6 space-y-3 text-[15px] text-ink" data-testid="price-breakdown">
      <div className="flex justify-between"><span className="underline decoration-ink/30 underline-offset-4">{t("w.nightsX", { p: b.price, n: b.nights })}</span><span data-testid="subtotal-amount">{money(b.subtotal)}</span></div>
      {b.cleaning > 0 && <div className="flex justify-between"><span>{t("w.cleaning")}</span><span>{money(b.cleaning)}</span></div>}
      <div className="flex justify-between border-t border-ink/15 pt-4 font-semibold"><span>{t("w.total")} (USD)</span><span data-testid="total-amount">{money(b.total)}</span></div>
    </div>
  );
};

export const BookingWidget = ({ prop, b, onRequest }) => {
  const { t, lang } = useI18n();
  const wide = useIsWide(1100);
  const [dOpen, setDOpen] = useState(false);
  const [gOpen, setGOpen] = useState(false);
  const f = (d) => (d ? format(d, "dd/MM/yyyy", { locale: DATE_LOCALES[lang] }) : t("w.add"));
  const guests = b.adults + b.children;
  const go = () => (b.ready ? onRequest() : setDOpen(true));
  return (
    <div id="book" data-testid="booking-widget" className="rounded-[28px] border border-ink/10 bg-white p-6 shadow-[0_24px_60px_rgba(44,32,22,0.10)] sm:p-7">
      <div className="flex items-end justify-between">
        <p><span data-testid="price-per-night" className="font-display text-4xl font-semibold text-ink">{money(b.price)}</span> <span className="text-muted2">/ {t("w.night")}</span></p>
        {prop?.rating > 0 && <p className="flex items-center gap-1 text-sm"><Star className="h-3.5 w-3.5 fill-ink" />{prop.rating} · <span className="text-muted2">{prop.reviews_count}</span></p>}
      </div>
      <div className="mt-6 overflow-hidden rounded-2xl border border-ink/25">
        <Popover open={dOpen} onOpenChange={setDOpen}>
          <PopoverTrigger asChild>
            <button data-testid="widget-dates-trigger" className="grid w-full grid-cols-2 text-left">
              <span className="border-r border-ink/25 p-3"><span className="block font-mono text-[10px] uppercase tracking-[0.2em]">{t("w.checkin")}</span><span data-testid="widget-checkin-value" className="text-sm">{f(b.range.from)}</span></span>
              <span className="p-3"><span className="block font-mono text-[10px] uppercase tracking-[0.2em]">{t("w.checkout")}</span><span data-testid="widget-checkout-value" className="text-sm">{f(b.range.to)}</span></span>
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-auto rounded-3xl bg-paper p-6" data-lenis-prevent>
            <BookingCalendar b={b} months={wide ? 2 : 1} />
            <div className="mt-4 flex justify-between">
              <button onClick={b.clear} className="text-sm font-semibold underline underline-offset-4">{t("cal.clear")}</button>
              <button data-testid="calendar-done-button" onClick={() => setDOpen(false)} className="rounded-xl bg-ink px-5 py-2 text-sm text-paper">{t("w.done")}</button>
            </div>
          </PopoverContent>
        </Popover>
        <Popover open={gOpen} onOpenChange={setGOpen}>
          <PopoverTrigger asChild>
            <button data-testid="widget-guests-trigger" className="flex w-full items-center justify-between border-t border-ink/25 p-3 text-left">
              <span><span className="block font-mono text-[10px] uppercase tracking-[0.2em]">{t("w.guests")}</span><span data-testid="widget-guests-value" className="text-sm">{guests} {guests === 1 ? t("l.guest") : t("l.guests")}</span></span>
              <ChevronDown className={`h-4 w-4 transition-transform ${gOpen ? "rotate-180" : ""}`} />
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-[320px] rounded-3xl bg-white p-5"><GuestPicker b={b} /></PopoverContent>
        </Popover>
      </div>
      <button
        data-testid="request-booking-button"
        onClick={go}
        className="mt-5 w-full rounded-2xl bg-cta py-4 text-[15px] font-semibold text-paper transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-cta/85"
      >
        {b.ready ? t("w.request") : t("w.check")}
      </button>
      <p className="mt-3 text-center text-xs text-muted2">{t("w.noCharge")}</p>
      <Breakdown b={b} />
    </div>
  );
};
